import React, { FC, useCallback, useMemo } from "react";
import { Divider, Typography, Grid } from "@mui/material";
import ExternalLinkArrowIcon from "@RHCommerceDev/icon-external-link-arrow";
import useTypographyStyles from "@RHCommerceDev/hooks/useTypographyStyles";
import clsx from "clsx";
import { ClassNameMap } from "@mui/material";
import memoize from "@RHCommerceDev/utils/memoize";
import EmailSignup from "./EmailSignup";
import { useCountry } from "@RHCommerceDev/hooks/useCountry";
import { useEnv } from "@RHCommerceDev/hooks/useEnv";
import yn from "yn";
import { usePageContentNoProxy } from "@RHCommerceDev/custom-providers/LocationProvider";
import { useFetchModel } from "@RHCommerceDev/hooks/useFetchModel";
import { DE, NA_COUNTRIES } from "@RHCommerceDev/utils/constants";
import { useRhUserAtomValue } from "@RHCommerceDev/hooks-atoms";
import { useLocalization } from "@RHCommerceDev/hooks/useLocalization";
import UserPreferencesV2 from "./UserPreferencesV2";

const AEMFooterContent: FC<{
  classes: ClassNameMap;
  brand?: string;
  isInEditor?: boolean;
}> = ({ classes, brand, isInEditor }) => {
  const {
    FEATURE_EMAIL_MARKETING_FOOTER_BE,
    FEATURE_EMAIL_MARKETING_FOOTER_FR
  } = useEnv();
  const ATGPagesToExcludePrefix = [
    "catalog-request.jsp",
    "contact-us.jsp",
    "sitemap.jsp"
  ];
  const prefix = useLocalization();
  const { pageContent } = !isInEditor
    ? usePageContentNoProxy()
    : useFetchModel(`/admin/footer-links`, false, false);
  const content = (pageContent as any)?.footerLinks;
  const { userType } = useRhUserAtomValue();
  const typographyclasses = useTypographyStyles({
    keys: ["rhBaseBody1", "rhBaseBody2", "rhBaseCaption", "uppercaseText"]
  });
  const pageContentArray = content ? Object.keys(content) : [];
  const country = useCountry();
  const isEmailMarketingFooterBE = yn(FEATURE_EMAIL_MARKETING_FOOTER_BE);
  const isEmailMarketingFooterFR = yn(FEATURE_EMAIL_MARKETING_FOOTER_FR);
  const environment = useEnv();

  const getPrefixUrl = useCallback(
    url => {
      const index = ATGPagesToExcludePrefix.findIndex(val => {
        return url.includes(val);
      });
      if (index === -1) return prefix;
      else return "";
    },
    [prefix]
  );

  const showEmailSignupComponent = useMemo((): boolean => {
    if (NA_COUNTRIES.includes(country)) {
      return true;
    }
    if (country === "GB") {
      return true;
    }
    if (country === DE) {
      return true;
    }
    if (isEmailMarketingFooterBE && country === "BE") {
      return true;
    }
    if (country === "ES") {
      return true;
    }
    if (isEmailMarketingFooterFR && country === "FR") {
      return true;
    }
    return false;
  }, [country, isEmailMarketingFooterBE, isEmailMarketingFooterFR]);

  return content ? (
    <footer className="px-8 py-10">
      <div
        className={clsx([
          classes.footerContainer,
          showEmailSignupComponent && classes.footerContainerwithEmail
        ])}
      >
        <div
          className={clsx([
            classes.separator,
            showEmailSignupComponent && classes.marketingSeparator
          ])}
        >
          <Divider />
        </div>
        <div>{showEmailSignupComponent && <EmailSignup />}</div>
        <div
          className={clsx([
            classes.footerMenus,
            showEmailSignupComponent && classes.footerMenusWithemail
          ])}
        >
          <Grid
            container
            justifyContent="space-between"
            className={classes.grid} /* "grid grid-wide" */
          >
            {pageContentArray?.map((parentKey, i) => {
              const rowArray = content[parentKey]
                ? content[parentKey].items
                  ? Array.isArray(content[parentKey].items)
                    ? content[parentKey].items
                    : Object.values(content[parentKey].items)
                  : Array.isArray(content[parentKey])
                  ? content[parentKey]
                  : Object.values(content[parentKey])
                : [];
              return (
                <div key={parentKey}>
                  <div className={classes.gridItemInner}>
                    <Typography
                      className={clsx([
                        typographyclasses.rhBaseBody2,
                        typographyclasses.uppercaseText,
                        classes.footerHeader
                      ])}
                    >
                      {rowArray && rowArray[0] && rowArray[0].groupLabel
                        ? rowArray[0].groupLabel
                        : parentKey}
                    </Typography>
                    <ul>
                      {content &&
                        content[parentKey] &&
                        Array.isArray(content[parentKey]) &&
                        content[parentKey].map(
                          (
                            { text: subCategoryName = "", url = "", linkType },
                            j
                          ) => {
                            const isExternalLink = url?.startsWith("https://");
                            let urlHasLocale = false;
                            if (prefix?.length) {
                              urlHasLocale = prefix?.startsWith("/")
                                ? url?.includes(prefix.substring(1))
                                : url?.includes(prefix);
                            }

                            const prefixToPrepend = urlHasLocale
                              ? ""
                              : getPrefixUrl(url);
                            let sanitizedUrl = isExternalLink
                              ? url
                              : `${prefixToPrepend}${url}`;
                            if (!isExternalLink) {
                              sanitizedUrl = sanitizedUrl?.startsWith("/")
                                ? sanitizedUrl
                                : `/${sanitizedUrl}`;
                            }

                            const isDisableSignUpEmailLink =
                              subCategoryName?.includes("Sign up for Emails") &&
                              showEmailSignupComponent;

                            return !(
                              url?.includes("gift-registry") &&
                              (brand === "TN" ||
                                userType === "CONTRACT" ||
                                userType === "TRADE")
                            ) && !isDisableSignUpEmailLink ? (
                              <li key={`child_sub_category_${i}_${j}`}>
                                <a
                                  target={
                                    linkType === "none" || isExternalLink
                                      ? "_blank"
                                      : "_self"
                                  }
                                  href={sanitizedUrl}
                                  data-analytics-parentid={parentKey}
                                >
                                  <Typography
                                    variant="caption"
                                    className={classes.footerItems}
                                  >
                                    {subCategoryName
                                      .trim()
                                      .split(/\r?\n/)
                                      .map((line, i, array) => (
                                        <span key={i}>
                                          {line}
                                          {i !== array.length - 1 && <br />}
                                        </span>
                                      ))}
                                    {isExternalLink && (
                                      <ExternalLinkArrowIcon
                                        width={6.21}
                                        height={6.21}
                                        aria-hidden="false"
                                      />
                                    )}
                                  </Typography>
                                </a>
                              </li>
                            ) : null;
                          }
                        )}
                    </ul>
                  </div>
                </div>
              );
            })}
          </Grid>
        </div>
      </div>
      <UserPreferencesV2 classes={classes} />
    </footer>
  ) : null;
};

export default memoize(AEMFooterContent);
